"use client";

/**
 * Cursor-paginated sequence list for the Configure modal's step 1 picker.
 *
 * The backend `GET /api/jobs/{job_id}/sequences` is cursor-based (`cursor` +
 * `per_page`, returns `{ sequences, next_cursor }`). The shared `useSequences`
 * hook only fetches a single page, so the picker uses its own `useInfiniteQuery`
 * here to accumulate pages behind a "Load more" control. Gated on `enabled` so a
 * closed-but-mounted modal stays idle.
 */

import { useInfiniteQuery } from "@tanstack/react-query";

import { listSequences } from "@/lib/api/generated";
import type { SequenceListItem } from "@/features/sequences/hooks/use-sequences";

const PER_PAGE = 15;

interface SequencesPage {
  sequences?: SequenceListItem[];
  next_cursor?: string | null;
}

export interface EnrollmentSequencesResult {
  sequences: SequenceListItem[];
  isLoading: boolean;
  isError: boolean;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: () => void;
}

export function useEnrollmentSequences(
  jobId: string | null,
  enabled: boolean,
): EnrollmentSequencesResult {
  const query = useInfiniteQuery({
    queryKey: ["enrollment-sequences", jobId],
    enabled: enabled && !!jobId,
    // Always re-validate enrollability when the modal (re)opens — a sequence
    // can flip to non-enrollable (sender mailbox disconnected, steps edited)
    // between opens, and we must never offer a stale "enrollable" row.
    staleTime: 0,
    refetchOnMount: "always",
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) =>
      listSequences(jobId as string, {
        cursor: pageParam ?? undefined,
        per_page: PER_PAGE,
      }),
    getNextPageParam: (lastPage) => {
      const cursor = (lastPage as SequencesPage)?.next_cursor;
      return cursor ?? undefined;
    },
  });

  const sequences = (query.data?.pages ?? []).flatMap((page) => {
    const raw = (page as SequencesPage)?.sequences;
    return Array.isArray(raw) ? raw : [];
  });

  return {
    sequences,
    isLoading: query.isLoading,
    isError: query.isError,
    hasNextPage: query.hasNextPage,
    isFetchingNextPage: query.isFetchingNextPage,
    fetchNextPage: () => {
      void query.fetchNextPage();
    },
  };
}
